import React, { useEffect, useRef } from 'react';

/**
 * The alert message, with its variables drawn as tags.
 *
 * The stored value is still the plain template the backend fills in
 * ("*{{name}}* ... {{value}}"); tags exist only on screen. A tag can be
 * removed with its X (or Backspace/Delete beside it) and dragged to another
 * place in the message; the palette below holds the rest, to drag in or click
 * to insert at the caret.
 */

const DRAG_TYPE = 'text/x-alert-var';
const TAG_RE = /\{\{(\w+)\}\}/g;

const TAG_CLS = 'mx-0.5 inline-flex h-5 select-none items-center gap-0.5 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-raised,var(--surface-base))] pl-1.5 pr-0.5 align-baseline text-[12px] leading-none text-[color:var(--text-primary)] cursor-grab';
const X_CLS = 'flex h-4 w-4 items-center justify-center rounded text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';

/** "a {{b}} c" -> [{text:'a '}, {tag:'b'}, {text:' c'}] */
export function parseMessage(template) {
  const out = [];
  let last = 0;
  const s = template || '';
  s.replace(TAG_RE, (m, name, at) => {
    if (at > last) out.push({ text: s.slice(last, at) });
    out.push({ tag: name });
    last = at + m.length;
    return m;
  });
  if (last < s.length) out.push({ text: s.slice(last) });
  return out;
}

function makeTag(name) {
  const el = document.createElement('span');
  el.setAttribute('contenteditable', 'false');
  el.setAttribute('draggable', 'true');
  el.setAttribute('data-var', name);
  el.setAttribute('role', 'group');
  el.setAttribute('aria-label', `${name} variable`);
  el.className = TAG_CLS;
  el.appendChild(document.createTextNode(name));
  const x = document.createElement('button');
  x.type = 'button';
  x.setAttribute('data-remove', '');
  x.setAttribute('aria-label', `Remove ${name}`);
  x.className = X_CLS;
  x.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 2l6 6M8 2L2 8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
  el.appendChild(x);
  return el;
}

function serialize(root) {
  let out = '';
  root.childNodes.forEach((n) => {
    if (n.nodeType === 3) out += n.nodeValue.replace(/​/g, '');
    else if (n.nodeType === 1) {
      if (n.dataset.var) out += `{{${n.dataset.var}}}`;
      else if (n.tagName === 'BR') { if (!n.hasAttribute('data-end')) out += '\n'; }
      else out += serialize(n);
    }
  });
  return out;
}

function fill(root, template) {
  root.textContent = '';
  parseMessage(template).forEach((p) => {
    root.appendChild(p.tag ? makeTag(p.tag) : document.createTextNode(p.text));
  });
  // Lets a trailing newline show as a line.
  const end = document.createElement('br');
  end.setAttribute('data-end', '');
  root.appendChild(end);
}

const isTag = (n) => n && n.nodeType === 1 && n.dataset && n.dataset.var;

export default function MessageEditor({ value, onChange, variables = [], ariaLabel = 'Message' }) {
  const ref = useRef(null);
  const last = useRef(null);
  const dragging = useRef(null);
  const saved = useRef(null);

  // Rebuild only when the value changed from outside (load, scope default).
  useEffect(() => {
    if (value !== last.current && ref.current) { fill(ref.current, value); last.current = value; }
  }, [value]);

  const emit = () => {
    const next = serialize(ref.current);
    last.current = next;
    onChange(next);
  };

  const remember = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current.contains(sel.anchorNode)) saved.current = sel.getRangeAt(0).cloneRange();
  };

  const insertAt = (range, node) => {
    const root = ref.current;
    let r = range;
    if (!r || !root.contains(r.startContainer)) {
      r = document.createRange();
      r.selectNodeContents(root);
      r.collapse(false);
      const endBr = root.querySelector('br[data-end]');
      if (endBr) { r.setStartBefore(endBr); r.collapse(true); }
    }
    r.deleteContents();
    r.insertNode(node);
    const after = document.createRange();
    after.setStartAfter(node);
    after.collapse(true);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(after);
    saved.current = after.cloneRange();
    emit();
  };

  const insertVar = (name) => {
    ref.current.focus();
    insertAt(saved.current, makeTag(name));
  };

  const caretFromPoint = (x, y) => {
    if (document.caretRangeFromPoint) return document.caretRangeFromPoint(x, y);
    if (document.caretPositionFromPoint) {
      const p = document.caretPositionFromPoint(x, y);
      if (!p) return null;
      const r = document.createRange();
      r.setStart(p.offsetNode, p.offset);
      r.collapse(true);
      return r;
    }
    return null;
  };

  const onDrop = (e) => {
    const name = e.dataTransfer.getData(DRAG_TYPE);
    if (!name) return;
    e.preventDefault();
    let r = caretFromPoint(e.clientX, e.clientY);
    if (r && !ref.current.contains(r.startContainer)) r = null;
    // Dropped on a tag: put it after, never inside.
    if (r) {
      const host = r.startContainer.nodeType === 1 ? r.startContainer : r.startContainer.parentNode;
      const tag = host && host.closest ? host.closest('[data-var]') : null;
      if (tag) { r = document.createRange(); r.setStartAfter(tag); r.collapse(true); }
    }
    const from = dragging.current;
    dragging.current = null;
    if (from && ref.current.contains(from)) {
      if (r && from.contains(r.startContainer)) return;
      // Resolve the spot before removing the dragged tag so the range stays valid.
      const marker = document.createElement('span');
      if (r) { r.insertNode(marker); } else { ref.current.insertBefore(marker, ref.current.querySelector('br[data-end]')); }
      from.remove();
      const spot = document.createRange();
      spot.setStartBefore(marker);
      spot.collapse(true);
      marker.remove();
      insertAt(spot, makeTag(name));
      return;
    }
    insertAt(r, makeTag(name));
  };

  const onKeyDown = (e) => {
    const root = ref.current;
    if (e.key === 'Enter') {
      e.preventDefault();
      const sel = window.getSelection();
      if (!sel.rangeCount) return;
      const r = sel.getRangeAt(0);
      r.deleteContents();
      const nl = document.createTextNode('\n');
      r.insertNode(nl);
      const after = document.createRange();
      after.setStartAfter(nl);
      after.collapse(true);
      sel.removeAllRanges(); sel.addRange(after);
      emit();
      return;
    }
    if (e.key !== 'Backspace' && e.key !== 'Delete') return;
    const sel = window.getSelection();
    if (!sel.rangeCount || !sel.isCollapsed) return;
    const r = sel.getRangeAt(0);
    const back = e.key === 'Backspace';
    const c = r.startContainer;
    const o = r.startOffset;
    let node = null;
    if (c === root) node = back ? root.childNodes[o - 1] : root.childNodes[o];
    else if (c.nodeType === 3) {
      if (back && o === 0) node = c.previousSibling;
      else if (!back && o === c.nodeValue.length) node = c.nextSibling;
    }
    // Skip empty text between the caret and the tag.
    while (node && node.nodeType === 3 && node.nodeValue === '') node = back ? node.previousSibling : node.nextSibling;
    if (isTag(node)) {
      e.preventDefault();
      node.remove();
      emit();
    }
  };

  const onClick = (e) => {
    const x = e.target.closest && e.target.closest('[data-remove]');
    if (x && ref.current.contains(x)) {
      e.preventDefault();
      x.closest('[data-var]').remove();
      emit();
    }
  };

  return (
    <div className="min-w-0 flex-1">
      <div
        ref={ref} role="textbox" aria-multiline="true" aria-label={ariaLabel} contentEditable suppressContentEditableWarning
        spellCheck={false}
        className="min-h-[72px] w-full whitespace-pre-wrap break-words rounded-md border border-[color:var(--border-default)] bg-transparent px-2 py-1.5 text-[13px] leading-6 text-[color:var(--text-primary)] focus:border-[color:var(--border-strong)] focus:outline-none"
        onInput={emit}
        onKeyDown={onKeyDown}
        onKeyUp={remember}
        onMouseUp={remember}
        onBlur={remember}
        onClick={onClick}
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          if (text) document.execCommand('insertText', false, text);
        }}
        onDragStart={(e) => {
          const tag = e.target.closest && e.target.closest('[data-var]');
          if (!tag) return;
          dragging.current = tag;
          e.dataTransfer.setData(DRAG_TYPE, tag.dataset.var);
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragEnd={() => { dragging.current = null; }}
        onDragOver={(e) => { if (e.dataTransfer.types.includes(DRAG_TYPE)) { e.preventDefault(); e.dataTransfer.dropEffect = dragging.current ? 'move' : 'copy'; } }}
        onDrop={onDrop}
      />
      <div className="mt-1.5 flex flex-wrap gap-1">
        {variables.map((v) => (
          <button
            key={v} type="button" draggable aria-label={`Insert ${v}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => insertVar(v)}
            onDragStart={(e) => { dragging.current = null; e.dataTransfer.setData(DRAG_TYPE, v); e.dataTransfer.effectAllowed = 'copy'; }}
            className="inline-flex h-5 cursor-grab items-center rounded-md border border-[color:var(--border-default)] px-1.5 text-[12px] leading-none text-[color:var(--text-secondary)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]"
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}
